import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { X, CheckCircle, Plus, AlertTriangle, Ticket, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { Avatar } from '../pages/Dashboard';
import { cn } from '../lib/utils';
import { TimePicker24h } from './TimePicker24h';

export default function PrivateClassModal({ onClose, onSaved, locations, allStudents }) {
  const [studentMode, setStudentMode] = useState('existing');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [privatePackages, setPrivatePackages] = useState({});
  const [selectedStudentPkgs, setSelectedStudentPkgs] = useState([]);
  const [showCreatePackage, setShowCreatePackage] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [newPackageForm, setNewPackageForm] = useState({ total_hours: '', total_price: '', paid_amount: '0' });
  const [classForm, setClassForm] = useState({
    datetime_start: format(new Date(), "yyyy-MM-dd'T'18:00"),
    duration_minutes: 60,
    location_id: locations[0]?.id || '',
    notes: '',
    content: '',
  });
  const [saving, setSaving] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  // Load all active private packages
  useEffect(() => {
    api.get('/packages').then(pkgs => {
      const map = {};
      pkgs.filter(p => p.type === 'private' && p.is_active).forEach(p => {
        if (!map[p.student_id]) map[p.student_id] = [];
        map[p.student_id].push(p);
      });
      setPrivatePackages(map);
    }).catch(() => {});
  }, []);

  // Load packages for selected student
  useEffect(() => {
    if (selectedStudentId && studentMode === 'existing') {
      const pkgs = privatePackages[selectedStudentId] || [];
      setSelectedStudentPkgs(pkgs);
      setShowCreatePackage(pkgs.length === 0);
      if (pkgs.length === 1) setSelectedPackageId(pkgs[0].id);
      else setSelectedPackageId('');
    }
  }, [selectedStudentId, privatePackages, studentMode]);

  const handleSave = async (force = false) => {
    if (studentMode === 'new' && (!newStudentForm.first_name || !newStudentForm.last_name)) {
      toast.error('Prénom et nom requis'); return;
    }
    if (studentMode === 'existing' && !selectedStudentId) {
      toast.error('Sélectionnez un élève'); return;
    }
    if (!classForm.location_id) {
      toast.error('Sélectionnez un lieu'); return;
    }

    const needNewPkg = showCreatePackage || studentMode === 'new';
    if (needNewPkg && (!newPackageForm.total_hours || !newPackageForm.total_price)) {
      toast.error('Renseignez le forfait (heures et prix)'); return;
    }
    if (!needNewPkg && !selectedPackageId) {
      toast.error('Sélectionnez un forfait'); return;
    }

    setSaving(true);
    try {
      let studentId = selectedStudentId;
      let packageId = selectedPackageId;

      if (studentMode === 'new') {
        const created = await api.post('/students', newStudentForm);
        studentId = created.id;
      }

      if (needNewPkg) {
        const pkg = await api.post('/packages', {
          student_id: studentId,
          type: 'private',
          total_hours: parseFloat(newPackageForm.total_hours),
          total_price: parseFloat(newPackageForm.total_price),
          paid_amount: parseFloat(newPackageForm.paid_amount) || 0,
        });
        packageId = pkg.id;
      }

      const cls = await api.post('/classes', {
        type: 'private',
        title: 'Cours privé',
        ...classForm,
        duration_minutes: parseInt(classForm.duration_minutes),
        force,
      });

      await api.post(`/classes/${cls.id}/students/${studentId}`);
      toast.success('Séance privée créée');
      onSaved();
    } catch (e) {
      if (e.status === 409 && e.conflicts) {
        setConflictData(e.conflicts);
      } else {
        toast.error(e.message || 'Erreur');
      }
    } finally { setSaving(false); }
  };

  const studentIdsWithPkg = new Set(Object.keys(privatePackages));
  const filtered = allStudents.filter(s =>
    `${s.first_name} ${s.last_name}`.toLowerCase().includes(studentSearch.toLowerCase())
  );
  const sorted = [
    ...filtered.filter(s => studentIdsWithPkg.has(s.id)),
    ...filtered.filter(s => !studentIdsWithPkg.has(s.id)),
  ];

  const showDetails = selectedStudentId || studentMode === 'new';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="private-class-modal">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="font-bold text-lg text-slate-900 font-manrope">Nouvelle séance privée</h2>
            <p className="text-xs text-slate-400 mt-0.5">Sélectionnez un élève et configurez la séance</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {/* ── Section 1 : Élève ── */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">1. Élève</div>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => { setStudentMode('existing'); setSelectedStudentId(''); }}
                className={cn('flex-1 h-10 rounded-xl text-sm font-bold transition-colors', studentMode === 'existing' ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                data-testid="mode-existing-btn"
              >
                Élève existant
              </button>
              <button
                onClick={() => setStudentMode('new')}
                className={cn('flex-1 h-10 rounded-xl text-sm font-bold transition-colors', studentMode === 'new' ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                data-testid="mode-new-btn"
              >
                Nouvel élève
              </button>
            </div>

            {studentMode === 'existing' && (
              <div>
                <input
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  placeholder="Rechercher un élève..."
                  className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                  data-testid="student-search-input"
                />
                <div className="max-h-44 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
                  {sorted.length === 0 && <p className="text-center py-6 text-slate-400 text-sm">Aucun élève</p>}
                  {sorted.map(s => {
                    const pkgs = privatePackages[s.id] || [];
                    const isSelected = selectedStudentId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setSelectedStudentId(s.id)}
                        className={cn('w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left', isSelected && 'bg-[#0288D1]/5')}
                        data-testid={`select-student-${s.id}`}
                      >
                        <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800">{s.first_name} {s.last_name}</div>
                          {pkgs.length > 0 && (
                            <div className="text-xs text-[#26A69A] font-semibold truncate">
                              {pkgs[0].total_hours}h privé · {pkgs[0].total_price - pkgs[0].paid_amount} CHF restants
                            </div>
                          )}
                        </div>
                        {isSelected && <CheckCircle size={16} className="text-[#0288D1] shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {studentMode === 'new' && (
              <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
                {[['first_name','Prénom *'],['last_name','Nom *'],['email','Email'],['phone','Téléphone']].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
                    <input
                      value={newStudentForm[key]}
                      onChange={e => setNewStudentForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                      data-testid={`new-student-${key}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section 2 : Forfait ── */}
          {showDetails && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">2. Forfait privé</div>

              {studentMode === 'existing' && selectedStudentPkgs.length > 0 && !showCreatePackage && (
                <div className="space-y-2 mb-3">
                  {selectedStudentPkgs.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackageId(pkg.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-colors text-left',
                        selectedPackageId === pkg.id ? 'border-[#0288D1] bg-[#0288D1]/5' : 'border-slate-200 hover:border-slate-300'
                      )}
                      data-testid={`select-package-${pkg.id}`}
                    >
                      <div>
                        <div className="text-sm font-semibold text-slate-800">{pkg.total_hours}h privé</div>
                        <div className="text-xs text-slate-500">{pkg.total_price} CHF · {pkg.total_price - pkg.paid_amount} CHF restants</div>
                      </div>
                      {selectedPackageId === pkg.id && <CheckCircle size={16} className="text-[#0288D1]" />}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCreatePackage(true)}
                    className="flex items-center gap-1 text-xs text-[#0288D1] font-semibold hover:underline mt-1"
                  >
                    <Plus size={12} /> Nouveau forfait
                  </button>
                </div>
              )}

              {(showCreatePackage || studentMode === 'new' || selectedStudentPkgs.length === 0) && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <div className="text-xs font-semibold text-slate-600">Créer un forfait</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[['total_hours','Heures','ex: 5'],['total_price','Prix CHF','ex: 550'],['paid_amount','Payé CHF','0']].map(([key, label, ph]) => (
                      <div key={key}>
                        <label className="block text-xs text-slate-500 mb-1">{label}</label>
                        <input
                          type="number" min="0" step={key === 'total_hours' ? '0.5' : '1'}
                          value={newPackageForm[key]}
                          onChange={e => setNewPackageForm(f => ({ ...f, [key]: e.target.value }))}
                          placeholder={ph}
                          className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                          data-testid={`pkg-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                  {studentMode === 'existing' && selectedStudentPkgs.length > 0 && (
                    <button onClick={() => setShowCreatePackage(false)} className="text-xs text-slate-500 hover:underline">
                      ← Utiliser un forfait existant
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Section 3 : Séance ── */}
          {showDetails && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">3. Séance</div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Date et heure</label>
                  <div className="flex gap-2 items-center">
                    <input type="date"
                      value={classForm.datetime_start ? classForm.datetime_start.slice(0, 10) : ''}
                      onChange={e => setClassForm(f => ({ ...f, datetime_start: e.target.value + 'T' + (f.datetime_start?.slice(11, 16) || '18:00') }))}
                      className="flex-1 h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                      data-testid="private-date-input"
                    />
                    <TimePicker24h
                      value={classForm.datetime_start?.slice(11, 16) || '18:00'}
                      onChange={t => setClassForm(f => ({ ...f, datetime_start: (f.datetime_start?.slice(0, 10) || '') + 'T' + t }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-2">Durée</label>
                  <div className="flex gap-1.5 flex-wrap mb-2">
                    {[{m:30,l:'30min'},{m:45,l:'45min'},{m:60,l:'1h'},{m:90,l:'1h30'},{m:120,l:'2h'},{m:150,l:'2h30'}].map(d => (
                      <button key={d.m} onClick={() => setClassForm(f => ({ ...f, duration_minutes: d.m }))}
                        className={cn('px-3 h-9 rounded-xl text-sm font-bold transition-colors',
                          classForm.duration_minutes === d.m ? 'bg-[#0288D1] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                        data-testid={`duration-${d.m}`}>
                        {d.l}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number" min="0" max="12"
                        value={Math.floor(classForm.duration_minutes / 60)}
                        onChange={e => {
                          const h = Math.max(0, parseInt(e.target.value) || 0);
                          setClassForm(f => ({ ...f, duration_minutes: h * 60 + (f.duration_minutes % 60) }));
                        }}
                        className="w-14 h-9 px-2 text-center bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                        data-testid="duration-hours-input"
                      />
                      <span className="text-xs text-slate-500 font-semibold">h</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number" min="0" max="55" step="5"
                        value={classForm.duration_minutes % 60}
                        onChange={e => {
                          const m = Math.min(55, Math.max(0, parseInt(e.target.value) || 0));
                          setClassForm(f => ({ ...f, duration_minutes: Math.floor(f.duration_minutes / 60) * 60 + m }));
                        }}
                        className="w-14 h-9 px-2 text-center bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                        data-testid="duration-minutes-input"
                      />
                      <span className="text-xs text-slate-500 font-semibold">min</span>
                    </div>
                    <span className="text-xs text-slate-400">
                      = {Math.floor(classForm.duration_minutes / 60)}h{String(classForm.duration_minutes % 60).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Lieu</label>
                  <select value={classForm.location_id} onChange={e => setClassForm(f => ({ ...f, location_id: e.target.value }))}
                    className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1]"
                    data-testid="private-location-select">
                    <option value="">-- Choisir --</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.venue_name} · {l.city}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optionnel)</label>
                  <input value={classForm.notes} onChange={e => setClassForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Remarques, objectifs..."
                    className="w-full h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20"
                    data-testid="private-notes-input" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-slate-100 flex gap-3 shrink-0">
          <button onClick={onClose} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200 transition-colors">
            Annuler
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving || (!selectedStudentId && studentMode !== 'new')}
            className="flex-1 h-12 rounded-xl bg-[#26A69A] text-white font-bold text-sm disabled:opacity-40 hover:bg-[#219e92] active:scale-95 transition-all shadow-sm"
            data-testid="create-private-session-btn"
          >
            {saving ? '...' : 'Créer la séance'}
          </button>
        </div>
      </div>

      {/* Conflict warning modal */}
      {conflictData && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="font-bold text-slate-900">Conflit détecté</div>
                <div className="text-xs text-slate-500">Ce créneau chevauche avec :</div>
              </div>
            </div>
            <div className="space-y-1.5 mb-5">
              {conflictData.map((c, i) => (
                <div key={c.id || c.title || i} className="flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-2 text-xs">
                  {c.type === 'festival'
                    ? <><Ticket size={13} className="text-amber-600 shrink-0" /><span className="font-semibold">{c.name}</span><span className="text-slate-400 ml-auto">{c.date_start} → {c.date_end}</span></>
                    : <><Calendar size={13} className="text-slate-500 shrink-0" /><span className="font-semibold">{c.title}</span><span className="text-slate-400 ml-auto">{(c.datetime_start || '').slice(11, 16)}</span></>
                  }
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConflictData(null)} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Annuler</button>
              <button
                onClick={() => { setConflictData(null); handleSave(true); }}
                className="flex-1 h-11 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm"
                data-testid="force-private-save-btn"
              >
                Forcer quand même
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
